import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export const MENU_IDS = {
  MAIN_MENU: 'main-menu',
  LANGUAGE_SWITCHER: 'language-switcher',
} as const;

type ActiveMenuContextType = {
  activeMenuId: string | null;
  openMenu: (menuId: string) => void;
  closeMenu: (menuId: string) => void;
  toggleMenu: (menuId: string) => void;
};

const ActiveMenuContext = createContext<ActiveMenuContextType | undefined>(undefined);

export function ActiveMenuProvider({ children }: { children: ReactNode }) {
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  const openMenu = useCallback((menuId: string) => {
    setActiveMenuId(menuId);
  }, []);

  const closeMenu = useCallback(
    (menuId: string) => {
      if (activeMenuId === menuId) {
        setActiveMenuId(null);
      }
    },
    [activeMenuId]
  );

  const toggleMenu = useCallback(
    (menuId: string) => {
      if (activeMenuId === menuId) {
        setActiveMenuId(null);
      } else {
        setActiveMenuId(menuId);
      }
    },
    [activeMenuId]
  );

  return (
    <ActiveMenuContext.Provider value={{ activeMenuId, openMenu, closeMenu, toggleMenu }}>
      {children}
    </ActiveMenuContext.Provider>
  );
}

export function useActiveMenu(): ActiveMenuContextType {
  const context = useContext(ActiveMenuContext);

  if (!context) {
    throw new Error('useActiveMenu must be used within ActiveMenuProvider');
  }

  return context;
}
